import * as Sentry from "@sentry/browser";
import Layout from "components/Layout";
import { locales } from "i18n";
import first from "lodash/first";
import flowRight from "lodash/flowRight";
import { fetchOrgByOdn } from "logic/orgs";
import createStore from "logic/store";
import withReduxSaga from "next-redux-saga";
import withRedux from "next-redux-wrapper";
import App from "next/app";
import React from "react";
import { IntlProvider } from "react-intl";
import { Provider } from "react-redux";
import pick from "lodash/pick";
import { ALLOWED_VARS } from "lib/misc";

import { version } from "../package.json";

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: "https://89edec5fc373467e9623ac8b6727b717@sentry.io/1781508",
  });
}

class TicketteerShopApp extends App {
  componentDidMount() {
    if (!process.browser) return;

    console.log(`ticketteer shop version ${version}`);
    localStorage.setItem("odn", this.props.odn);
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.NODE_ENV !== "production") return;

    Sentry.withScope((scope) => {
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key]);
      });

      Sentry.captureException(error);
    });

    super.componentDidCatch(error, errorInfo);
  }

  static async getInitialProps({ Component, router, ctx }) {
    let pageProps = {
      odn: ctx.query.odn,
      env: pick(process.env, ALLOWED_VARS),
    };
    let locale;

    if (ctx.asPath.includes("/adm/") && !ctx.asPath.includes("/box-office/")) {
      ctx.res.writeHead(302, { Location: `/box-office${ctx.asPath}` });
      ctx.res.end();
      return;
    }
    if (ctx.asPath.includes("/box-office/box-office/")) {
      ctx.res.writeHead(302, {
        Location: `/box-office${ctx.asPath.replace(
          "/box-office/box-office",
          ""
        )}`,
      });
      ctx.res.end();
      return;
    }

    if (ctx.pathname === "/[odn]" && ctx.query.odn === "pos") {
      ctx.res.writeHead(302, { Location: `/box-office/pos` });
      ctx.res.end();
      return;
    }

    if (ctx.req && ctx.req.headers)
      locale = ctx.req.headers["accept-language"]
        ? first(ctx.req.headers["accept-language"].split("-"))
        : "de";
    else {
      const detectBrowserLanguage = require("detect-browser-language");
      locale = detectBrowserLanguage();
    }

    if (typeof Component.getInitialProps === "function") {
      pageProps = await Component.getInitialProps(ctx);
    }

    if (ctx.pathname.includes("/vg/")) ctx.store.dispatch(fetchOrgByOdn("vg"));
    else ctx.store.dispatch(fetchOrgByOdn(ctx.query.odn));
    return {
      pageProps, // required to inject into props
      ...pageProps, // required to make env vars work. don't know why (zet)
      router,
      locale,
    };
  }

  render() {
    const { Component, pageProps, locale, store } = this.props;
    const safeLocale = first(locale.split(","));
    return (
      <Provider store={store}>
        <IntlProvider
          locale={safeLocale}
          messages={locales[safeLocale]}
          key={safeLocale}
        >
          <Layout {...pageProps}>
            <Component {...pageProps} />
          </Layout>
        </IntlProvider>
      </Provider>
    );
  }
}

const enhance = flowRight(withRedux(createStore), withReduxSaga);

export default enhance(TicketteerShopApp);
